// What a tap and a session end write to the session record on this phone.
//
// Pure, so the rows that decide what uploads and what the summaries show can be
// tested without a browser. Tracker.tsx runs these inside mutateSession.

import type { Applied } from "./tapper.ts";
import type { Block, TrackerState } from "./tracker.ts";
import type { LocalSession } from "./queue.ts";

/** Replaces the block with the same id, or adds it. */
function upsert(list: Block[], b: Block) {
  const i = list.findIndex((x) => x.id === b.id);
  if (i >= 0) list[i] = b; else list.push(b);
}

/**
 * The record after one saved tap. The queue gets exactly the rows to upload;
 * the history keeps every block, so a summary after a resume still counts the
 * ones that already uploaded and left the queue. A record written before the
 * history existed starts it from whatever is still queued.
 */
export function recordTap(stored: LocalSession, next: TrackerState, outcome: Applied, at: number): LocalSession {
  const blocks = [...stored.pendingBlocks];
  const taps = [...stored.pendingTaps];
  const history = [...(stored.history ?? stored.pendingBlocks)];
  if (outcome.kind === "opened") {
    if (outcome.closed) { upsert(blocks, outcome.closed); upsert(history, outcome.closed); }
    upsert(blocks, outcome.block); upsert(history, outcome.block);
    for (const t of outcome.block.taps) taps.push({ ...t, eventId: outcome.block.id });
  } else {
    upsert(blocks, outcome.block); upsert(history, outcome.block);
    taps.push({ ...outcome.tap, eventId: outcome.block.id });
  }
  return { ...stored, pendingBlocks: blocks, pendingTaps: taps, history, nextSeq: next.nextSeq, lastSeenAt: at };
}

/**
 * The record after End session. The last block is closed in the queue, and the
 * history is dropped: the end summary has been taken by then, and since every
 * tap rewrites the whole record, keeping it would leave each ended session on
 * the phone heavier for good.
 */
export function recordEnd(stored: LocalSession, closed: Block | null, at: number): LocalSession {
  const blocks = [...stored.pendingBlocks];
  if (closed) upsert(blocks, closed);
  return { ...stored, pendingBlocks: blocks, history: undefined, endedAt: Math.max(closed?.endedAt ?? at, stored.startedAt) };
}
